import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { Plus, X } from "lucide-react";

const DIFF = [
  ["easy", "Easy", "bg-emerald-500/25 text-emerald-800 dark:text-emerald-200"],
  ["medium", "Medium", "bg-sky-500/25 text-sky-800 dark:text-sky-200"],
  ["hard", "Hard", "bg-amber-500/30 text-amber-800 dark:text-amber-200"],
];
const labelCls = "text-xs text-muted-foreground font-normal";

function NumberField({ id, label, value, onChange, min, max, suffix }) {
  return (
    <div className="space-y-1.5 min-w-0">
      <Label htmlFor={id} className={labelCls}>{label}</Label>
      <div className="relative">
        <Input id={id} type="number" min={min} max={max} value={value} onChange={(e) => onChange(Number(e.target.value))} data-testid={id} className={cn("hs-mono", suffix && "pr-12")} />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">{suffix}</span>}
      </div>
    </div>
  );
}

function DifficultyMix({ bp }) {
  const { config, set, total, countValid } = bp;
  const sum = config.easy + config.medium + config.hard;
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">Difficulty mix</span>
        <span id="qa-count-status" data-testid="qa-count-status" className={countValid ? "text-primary" : "text-amber-600 dark:text-amber-400"}>{sum} / {total} questions{countValid ? "" : " · must match"}</span>
      </div>
      <div className="flex h-9 rounded-lg overflow-hidden border border-border" aria-hidden="true">
        {sum === 0 && <div className="flex-1 bg-muted" />}
        {DIFF.map(([k, label, cls]) => config[k] > 0 && <div key={k} style={{ flexGrow: config[k] }} className={cn("flex items-center justify-center hs-mono text-xs font-semibold min-w-[68px] transition-[flex-grow]", cls)}>{label} {config[k]}</div>)}
      </div>
      <div className="grid grid-cols-3 gap-2">
        {DIFF.map(([k, label]) => (
          <label key={k} className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="w-12 shrink-0">{label}</span>
            <Input type="number" min="0" max="30" value={config[k]} onChange={(e) => set(k, Number(e.target.value))} data-testid={`qa-${k}`} aria-label={`${label} questions`} className="h-8 hs-mono" />
          </label>
        ))}
      </div>
    </div>
  );
}

export function BlueprintForm({ bp, readOnly }) {
  const { config, set, timed, setTimed } = bp;
  return (
    <fieldset disabled={readOnly} className="space-y-5 min-w-0">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="space-y-1.5 col-span-2 lg:col-span-1">
          <Label htmlFor="qa-seniority" className={labelCls}>Candidate level</Label>
          <Input id="qa-seniority" data-testid="qa-seniority" value={config.seniority} maxLength={80} onChange={(e) => set("seniority", e.target.value)} />
        </div>
        <NumberField id="qa-mcq_count" label="Multiple choice" value={config.mcq_count} min={0} max={30} onChange={(v) => set("mcq_count", v)} />
        <NumberField id="qa-subjective_count" label="Written" value={config.subjective_count} min={0} max={30} onChange={(v) => set("subjective_count", v)} />
        <NumberField id="qa-duration_min" label="Time budget" value={config.duration_min} min={5} max={180} suffix="min" onChange={(v) => set("duration_min", v)} />
      </div>
      <DifficultyMix bp={bp} />
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="qa-instructions" className={labelCls}>Guidance for questions</Label>
          <Textarea id="qa-instructions" data-testid="qa-instructions" value={config.instructions} onChange={(e) => set("instructions", e.target.value)} maxLength={3000} rows={3} placeholder="E.g. use realistic troubleshooting scenarios; test practical judgement rather than terminology." />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="qa-avoid" className={labelCls}>Topics to avoid</Label>
          <Textarea id="qa-avoid" data-testid="qa-avoid" value={config.avoid_topics} onChange={(e) => set("avoid_topics", e.target.value)} maxLength={1000} rows={3} placeholder="E.g. vendor licensing trivia, obscure syntax recall." />
        </div>
      </div>
      <label className="flex items-center justify-between gap-3 rounded-lg border border-border px-3.5 py-2.5 cursor-pointer">
        <span className="min-w-0">
          <span className="block text-sm font-medium">Enforce the time limit</span>
          <span className="block text-xs text-muted-foreground">{timed ? `Candidates see a ${config.duration_min}-minute countdown.` : "Untimed. The budget only guides how long questions are."}</span>
        </span>
        <Switch checked={timed} onCheckedChange={setTimed} data-testid="qa-timed" />
      </label>
    </fieldset>
  );
}

export function WeightsCard({ bp, readOnly }) {
  const { config, set, weight } = bp;
  const comps = config.competencies;
  const update = (i, patch) => set("competencies", comps.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  return (
    <section className="hs-card p-5 space-y-3" data-testid="qa-weights">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Competency weights</h3>
        <span data-testid="qa-weight-status" className={cn("hs-mono text-xs", weight === 100 ? "text-primary" : "text-amber-600 dark:text-amber-400")}>{weight}% / 100%</span>
      </div>
      <fieldset disabled={readOnly} className="space-y-1">
        {comps.map((c, i) => (
          <div key={i} className="group flex items-center gap-1.5">
            <Input aria-label={`Competency ${i + 1}`} data-testid={`qa-competency-${i}`} value={c.name} maxLength={100} onChange={(e) => update(i, { name: e.target.value })}
              className="h-8 min-w-0 text-sm border-transparent bg-transparent px-1.5 hover:border-border focus-visible:border-border" />
            <Input aria-label={`Weight ${i + 1}`} type="number" min="1" max="100" data-testid={`qa-weight-${i}`} value={c.weight} onChange={(e) => update(i, { weight: Number(e.target.value) })} className="h-8 w-16 shrink-0 hs-mono text-right" />
            <button type="button" aria-label={`Remove competency ${i + 1}`} data-testid={`qa-remove-${i}`} disabled={comps.length === 1} onClick={() => set("competencies", comps.filter((_, j) => j !== i))} className="ai-icon-button opacity-50 group-hover:opacity-100 focus-visible:opacity-100"><X className="h-3.5 w-3.5" /></button>
          </div>
        ))}
        <button type="button" disabled={comps.length >= 8} onClick={() => set("competencies", [...comps, { name: "", weight: 0 }])} data-testid="qa-add-competency" className="text-xs text-primary hover:underline disabled:opacity-40 inline-flex items-center gap-1 pt-1.5"><Plus className="h-3 w-3" />Add competency</button>
      </fieldset>
      <p className="text-xs text-muted-foreground">Pre-filled from the role. Each competency gets at least one question.</p>
    </section>
  );
}
